import { useEffect, useState } from 'react'
import { postDataAPI } from '../../../lib/api'
import type { SignupUser } from '../SignupPage'
import OnboardingShell from './OnboardingShell'
import TextField from '../../../components/ui/TextField'
import { OnboardingStep } from './steps'

export default function Step04Username({
  setStep,
  user,
  setUser,
}: {
  setStep: (n: number) => void
  user: SignupUser
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
}) {
  const [status, setStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setStatus('idle')
    setError(null)
  }, [])

  const check = async (username: string) => {
    if (!username.trim()) {
      setStatus('idle')
      return
    }
    setStatus('checking')
    setError(null)
    try {
      const res = await postDataAPI('auth/checkUserName', { username })
      const exists = !!res?.data?.exist
      setStatus(exists ? 'taken' : 'available')
    } catch (err: any) {
      setStatus('idle')
      setError(err?.response?.data?.message || err?.message || 'Failed to check username')
    }
  }

  const canNext = status === 'available' && !!user.username?.trim()

  return (
    <OnboardingShell title="What should we call you?" footer="Choose a unique username that represents you or your brand.">
      <div className="space-y-5">
        <div>
          <TextField
            value={user.username || ''}
            onChange={(raw) => {
              const v = raw.trim().replace(/\s+/g, '')
              setUser((prev) => ({ ...prev, username: v, domain: v }))
              if (v.length >= 2) check(v)
              else setStatus('idle')
            }}
            placeholder="Add a username"
            error={error}
          />
          <div className="mt-2 text-sm">
            {status === 'checking' ? <span className="text-gray-500">Checking…</span> : null}
            {status === 'available' ? <span className="text-green-600">Available</span> : null}
            {status === 'taken' ? <span className="text-red-600">Taken</span> : null}
          </div>
        </div>

        <div className="flex gap-4 pt-2">
          <button type="button" onClick={() => setStep(OnboardingStep.FindPresence)} className="ds-btn ds-btn-secondary flex-1">
            Go back
          </button>
          <button type="button" disabled={!canNext} onClick={() => setStep(OnboardingStep.AddReleases)} className="ds-btn ds-btn-primary flex-1">
            Next
          </button>
        </div>
      </div>
    </OnboardingShell>
  )
}

